<script setup lang="ts">
import type { WhatsNewPayload } from "~/composables/useWhatsNew";

const props = defineProps<{
  payload: WhatsNewPayload;
  preview?: boolean;
}>();

const emit = defineEmits(["close"]);

const handleClose = () => {
  if (!props.preview) {
    useWhatsNew().markSeen(props.payload.id);
  }
  emit("close");
};

const changelogUrl = computed(() => props.payload.changelogUrl || "/changelog");
const goToChangelog = async () => {
  handleClose();
  await navigateTo(changelogUrl.value);
};
</script>

<template>
  <BaseLayoutModal width="xl" desktop-max-width-class="sm:max-w-[50.4rem]" @close="handleClose">
    <template #header>
      <div class="flex items-center gap-3">
        <span class="text-base font-semibold">{{ payload.title }}</span>
        <span class="rounded-full border border-neutral-500 px-2 py-0.5 text-xs text-neutral-300">
          v{{ payload.id }}
        </span>
        <span v-if="preview" class="rounded-full bg-primary-500/20 px-2 py-0.5 text-xs text-primary-300">
          Preview
        </span>
      </div>
    </template>

    <template #content>
      <div class="space-y-4">
        <img
          v-if="payload.bannerImage"
          :src="payload.bannerImage"
          alt="What's new banner"
          class="h-36 w-full rounded-md object-cover border border-white/10"
        />

        <div
          v-if="payload.summary"
          class="text-neutral-200 leading-6 whitespace-normal text-justify [&_a]:text-[#f3d5a6] [&_a]:underline [&_a]:decoration-2 [&_a]:underline-offset-2 [&_a:hover]:text-[#ffe8c5] [&_b]:font-semibold [&_strong]:font-semibold [&_li]:ml-5 [&_ol]:list-decimal [&_ul]:list-disc"
          v-html="payload.summary" />

        <div v-if="payload.items?.length" class="space-y-2">
          <p class="text-sm font-semibold uppercase tracking-[0.16em] text-neutral-300">
            More changes
          </p>
          <ul class="list-disc pl-5 text-neutral-200 text-justify space-y-2">
            <li v-for="item in payload.items" :key="item">
              {{ item }}
            </li>
          </ul>
        </div>

        <div class="space-y-3 pt-2">
          <p class="text-sm text-neutral-300">
            Check the
            <a
              :href="changelogUrl"
              class="mx-1 text-[#f3d5a6] underline decoration-2 underline-offset-2 hover:text-[#ffe8c5]"
              @click.prevent="goToChangelog">
              changelog
            </a>
            for more info.
          </p>
          <div class="flex justify-end">
          <BaseButtonSubmit @click="handleClose">
            <template #text>
              <span>Got it</span>
            </template>
          </BaseButtonSubmit>
          </div>
        </div>
      </div>
    </template>
  </BaseLayoutModal>
</template>
